/* Accounts: the fleet leaderboard. Three views — Combined (Revenue and its
   Seller split), AI, Team — each rankable by any column. A row's bar shows who
   earned it; opening a row shows that account's own ledger and its operators.
   Fansly accounts have no synced Revenue yet, so their money reads "—". The
   toolbar renders into the tab bar (``toolbarSlot``). */

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ChatStatsResponse, StatisticsResponse, StatSeller } from '../../../api/client';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Icon, Seg } from '../../ChatAssistant/compact/kit';
import { aiActivity, type AiActivity } from './derive';
import { SELLER_COLOR, downloadCsv, fmt, ledgerMoney, pct, pick, ratio, share, usd, usd2, type MoneyMode } from './model';

type View = 'all' | 'ai' | 'team';
type Platform = 'all' | 'onlyfans' | 'fansly';

interface Acc {
  id: string;
  name: string;
  fansly: boolean;
  money: Record<StatSeller, number>;
  revenue: number;
  sold: { ai: number; team: number; unknown: number };
  ai: AiActivity;
  team: { operators: number; messages: number; chats: number; ppv_sent: number; ppv_sold: number };
}

interface Col { id: string; label: string; tip?: string; value: (a: Acc) => number | null; show: (v: number) => string; team?: boolean }

interface Props {
  stats: StatisticsResponse | null;
  chat: ChatStatsResponse | null;
  mode: MoneyMode;
  hasCrm: boolean;
  toolbarSlot: HTMLElement | null;
}

const TOP = 12;

export function AccountsTable({ stats, chat, mode, hasCrm, toolbarSlot }: Props) {
  const { t } = useTranslation('statsUnified');
  const navigate = useNavigate();
  const [view, setView] = useState<View>('all');
  const [platform, setPlatform] = useState<Platform>('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ col: string; dir: 1 | -1 }>({ col: 'revenue', dir: -1 });
  const [open, setOpen] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const effView: View = !hasCrm && view === 'team' ? 'all' : view;

  const accounts: Acc[] = useMemo(() => {
    const money = new Map((stats?.money.accounts ?? []).map((a) => [a.account_id, a]));
    const ai = new Map((chat?.accounts ?? []).map((a) => [a.account_id, a]));
    const team = new Map((stats?.team_accounts ?? []).map((a) => [a.account_id, a]));
    const fansly = new Set(stats?.money.fansly_account_ids ?? []);
    const ids = new Set([...money.keys(), ...ai.keys(), ...team.keys()]);
    return [...ids].map((id) => {
      const m = money.get(id);
      const sellers = { ai: pick(m?.sellers.ai, mode), team: hasCrm ? pick(m?.sellers.team, mode) : 0, unknown: pick(m?.sellers.unknown, mode), not_in_chat: pick(m?.sellers.not_in_chat, mode) };
      return {
        id,
        name: m?.name || ai.get(id)?.name || id,
        fansly: fansly.has(id),
        money: sellers,
        revenue: pick(m?.revenue, mode),
        sold: m?.ppv_sold ?? { ai: 0, team: 0, unknown: 0 },
        ai: aiActivity(ai.get(id)),
        team: team.get(id) ?? { operators: 0, messages: 0, chats: 0, ppv_sent: 0, ppv_sold: 0 },
      };
    });
  }, [stats, chat, mode, hasCrm]);

  const chats = (a: Acc) => a.ai.chats + (hasCrm ? a.team.chats : 0);
  const COLS: Record<View, Col[]> = {
    all: [
      { id: 'revenue', label: t('accounts.cols.revenue'), tip: t('accounts.tips.revenue'), value: (a) => a.revenue, show: usd },
      { id: 'ai', label: t('sellers.ai'), value: (a) => a.money.ai, show: usd },
      { id: 'team', label: t('sellers.team'), value: (a) => a.money.team, show: usd, team: true },
      { id: 'unknown', label: t('sellers.unknown'), tip: t('sellers.unknownTip'), value: (a) => a.money.unknown, show: usd },
      { id: 'chats', label: t('accounts.cols.chats'), tip: t('accounts.tips.chats'), value: chats, show: fmt },
      { id: 'paid', label: t('accounts.cols.paid'), tip: t('accounts.tips.paid'), value: (a) => ratio(a.ai.payments + (hasCrm ? a.team.ppv_sold : 0), chats(a)) * 100, show: (v) => pct(v) },
      { id: 'rpc', label: t('accounts.cols.revPerChat'), tip: t('accounts.tips.revPerChat'), value: (a) => ratio(a.money.ai + a.money.team, chats(a)), show: usd2 },
    ],
    ai: [
      { id: 'ai', label: t('accounts.cols.aiRevenue'), value: (a) => a.money.ai, show: usd },
      { id: 'aiChats', label: t('accounts.cols.chats'), value: (a) => a.ai.chats, show: fmt },
      { id: 'aiMsgs', label: t('accounts.cols.messages'), value: (a) => a.ai.messages, show: fmt },
      { id: 'asks', label: t('accounts.cols.tipAsks'), value: (a) => a.ai.asks, show: fmt },
      { id: 'aiSold', label: t('accounts.cols.ppvSold'), value: (a) => a.sold.ai, show: fmt },
      { id: 'aiPaid', label: t('accounts.cols.paid'), tip: t('accounts.tips.aiPaid'), value: (a) => ratio(a.ai.payments, a.ai.chats) * 100, show: (v) => pct(v) },
      { id: 'newChats', label: t('accounts.cols.newChats'), value: (a) => a.ai.newChats, show: fmt },
    ],
    team: [
      { id: 'team', label: t('accounts.cols.teamRevenue'), value: (a) => a.money.team, show: usd },
      { id: 'ops', label: t('accounts.cols.operators'), value: (a) => a.team.operators, show: fmt },
      { id: 'tChats', label: t('accounts.cols.chats'), value: (a) => a.team.chats, show: fmt },
      { id: 'tMsgs', label: t('accounts.cols.messages'), value: (a) => a.team.messages, show: fmt },
      { id: 'tSent', label: t('accounts.cols.ppvSent'), value: (a) => a.team.ppv_sent, show: fmt },
      { id: 'tSold', label: t('accounts.cols.ppvSold'), value: (a) => a.team.ppv_sold, show: fmt },
      { id: 'tRate', label: t('accounts.cols.soldRate'), value: (a) => ratio(a.team.ppv_sold, a.team.ppv_sent) * 100, show: (v) => pct(v) },
    ],
  };
  const cols = COLS[effView];
  const sortCol = cols.find((c) => c.id === sort.col) ?? cols[0]!;
  const needle = q.trim().toLowerCase();
  const list = accounts
    .filter((a) => (platform === 'all' || (platform === 'fansly') === a.fansly) && (!needle || a.name.toLowerCase().includes(needle)))
    .sort((x, y) => ((sortCol.value(x) ?? -1) - (sortCol.value(y) ?? -1)) * sort.dir || x.name.localeCompare(y.name));
  const shown = showAll ? list : list.slice(0, TOP);
  const barKeys: StatSeller[] = effView === 'all' ? ['ai', 'team', 'unknown'] : effView === 'ai' ? ['ai'] : ['team'];
  const maxBar = Math.max(1, ...accounts.map((a) => barKeys.reduce((s, k) => s + a.money[k], 0)));
  const grid = `24px minmax(200px,1.7fr) repeat(${cols.length}, minmax(72px,1fr)) 14px`;

  const total = (c: Col): string => {
    if (c.team && !hasCrm) return '—';
    const S = (fn: (a: Acc) => number) => list.reduce((s, a) => s + fn(a), 0);
    switch (c.id) {
      case 'paid': return pct(ratio(S((a) => a.ai.payments + (hasCrm ? a.team.ppv_sold : 0)), S(chats)) * 100);
      case 'rpc': return usd2(ratio(S((a) => a.money.ai + a.money.team), S(chats)));
      case 'aiPaid': return pct(ratio(S((a) => a.ai.payments), S((a) => a.ai.chats)) * 100);
      case 'tRate': return pct(ratio(S((a) => a.team.ppv_sold), S((a) => a.team.ppv_sent)) * 100);
      case 'ops': return fmt(new Set((stats?.team ?? []).filter((r) => r.account_ids.some((id) => list.some((a) => a.id === id))).map((r) => r.operator_id)).size);
      default: return c.show(S((a) => c.value(a) ?? 0));
    }
  };

  const exportCsv = () => {
    const header = [t('accounts.account'), t('accounts.platform'), ...cols.map((c) => c.label)];
    const body = list.map((a) => [a.name, a.fansly ? 'Fansly' : 'OnlyFans', ...cols.map((c) => { const v = c.value(a); return (c.team && !hasCrm) || v === null ? '' : c.show(v); })]);
    downloadCsv(`statistics-accounts-${stats?.range.start ?? ''}_${stats?.range.end ?? ''}.csv`, [header, ...body]);
  };

  const toolbar = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Icon name="search" size={12} color={C.muted} style={{ position: 'absolute', left: 9, pointerEvents: 'none' }} />
        <input id="stx-accounts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('accounts.search')} style={{ height: 28, width: 160, padding: '0 10px 0 28px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: f(500, 12), outline: 'none', boxSizing: 'border-box' }} />
      </div>
      <Seg size="sm" value={platform} onChange={setPlatform} options={[{ id: 'all', label: t('accounts.platformAll') }, { id: 'onlyfans', label: 'OnlyFans' }, { id: 'fansly', label: 'Fansly' }]} />
      <Seg
        size="sm"
        value={effView}
        onChange={(v) => { setView(v); setSort({ col: COLS[v][0]!.id, dir: -1 }); }}
        options={[
          { id: 'all' as View, label: <><Icon name="layers" size={11} />{t('accounts.viewAll')}</>, title: t('accounts.viewAllTip') },
          { id: 'ai' as View, label: <><Icon name="sparkles" size={11} />{t('sellers.ai')}</> },
          ...(hasCrm ? [{ id: 'team' as View, label: <><Icon name="users" size={11} />{t('sellers.team')}</> }] : []),
        ]}
      />
      <button type="button" onClick={exportCsv} className="cpt-hover-border" style={{ height: 28, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: `1px solid ${C.borderHi}`, borderRadius: 6, color: 'var(--om-body)', font: f(500, 12), whiteSpace: 'nowrap', cursor: 'pointer' }}>
        <Icon name="download" size={12} color={C.muted} />CSV
      </button>
    </div>
  );

  const teamOn = (id: string) => (stats?.team ?? [])
    .map((r) => ({ name: r.operator_username, v: ledgerMoney(r.sales_by_account[id] ?? 0, mode), worked: r.account_ids.includes(id) }))
    .filter((o) => o.worked)
    .sort((a, b) => b.v - a.v);

  return (
    <>
      {toolbarSlot && createPortal(toolbar, toolbarSlot)}
      <div className="cpt-thin-scroll" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflowX: 'auto' }}>
        <div style={{ minWidth: 960 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px 12px', padding: '12px 14px 10px', flexWrap: 'wrap' }}>
            <span style={{ font: f(600, 13), color: C.text, whiteSpace: 'nowrap' }}>{t('accounts.count', { count: list.length })}</span>
            <span style={{ font: f(400, 11.5), color: C.muted, flex: '1 1 240px' }}>{t(`accounts.hint.${effView}`)}</span>
            <div style={{ display: 'flex', gap: 12 }}>
              {barKeys.map((k) => (
                <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 11.5), color: C.sub, whiteSpace: 'nowrap' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: k === 'team' && !hasCrm ? 'var(--om-border-hi)' : SELLER_COLOR[k] }} />{t(`sellers.${k}`)}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center', height: 32, padding: '0 14px', borderTop: `1px solid ${C.divider}`, borderBottom: `1px solid ${C.divider}` }}>
            <span />
            <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{t('accounts.account')}</span>
            {cols.map((c) => {
              const on = c.id === sortCol.id;
              return (
                <button key={c.id} type="button" title={c.tip ?? t('accounts.rankBy', { column: c.label })} onClick={() => setSort(on ? { col: c.id, dir: (sort.dir * -1) as 1 | -1 } : { col: c.id, dir: -1 })} className="cpt-hover-text" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, border: 0, background: 'transparent', padding: 0, font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: on ? C.purpleLt : C.muted, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {c.label}<Icon name={on && sort.dir === 1 ? 'arrow-up' : 'arrow-down'} size={11} style={{ opacity: on ? 1 : 0.35 }} />
                </button>
              );
            })}
            <span />
          </div>
          {shown.map((a, i) => {
            const isOpen = open === a.id;
            const bg = isOpen ? 'var(--om-panel)' : 'transparent';
            const chatSales = a.money.ai + a.money.team + a.money.unknown;
            const ops = isOpen ? teamOn(a.id) : [];
            const tm = (v: string) => (hasCrm ? v : '—');
            const led: [string, string[], boolean?][] = [
              [t('ledger.soldInChat'), [usd(a.money.ai), tm(usd(a.money.team)), usd(a.money.unknown), usd(chatSales)], true],
              [t('ledger.share'), [`${share(a.money.ai, chatSales)}%`, tm(`${share(a.money.team, chatSales)}%`), `${share(a.money.unknown, chatSales)}%`, '100%']],
              [t('ledger.ppvSent'), [fmt(a.ai.ppvSent), tm(fmt(a.team.ppv_sent)), '—', fmt(a.ai.ppvSent + (hasCrm ? a.team.ppv_sent : 0))]],
              [t('ledger.ppvSold'), [fmt(a.sold.ai), tm(fmt(a.sold.team)), fmt(a.sold.unknown), fmt(a.sold.ai + a.sold.team + a.sold.unknown)]],
              [t('ledger.chats'), [fmt(a.ai.chats), tm(fmt(a.team.chats)), '—', fmt(chats(a))]],
              [t('ledger.messages'), [fmt(a.ai.messages), tm(fmt(a.team.messages)), '—', fmt(a.ai.messages + (hasCrm ? a.team.messages : 0))]],
              [t('ledger.revenuePerChat'), [a.ai.chats ? usd2(a.money.ai / a.ai.chats) : '—', tm(a.team.chats ? usd2(a.money.team / a.team.chats) : '—'), '—', chats(a) ? usd2((a.money.ai + a.money.team) / chats(a)) : '—']],
            ];
            return (
              <div key={a.id} style={{ display: 'flex', flexDirection: 'column', borderTop: i ? `1px solid ${C.divider}` : 0 }}>
                <div role="button" tabIndex={0} onClick={() => setOpen(isOpen ? null : a.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(isOpen ? null : a.id); } }} className="cpt-hover-row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center', minHeight: 46, padding: '0 14px', cursor: 'pointer', background: bg }}>
                  <span style={{ font: f(500, 11.5), color: C.muted, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                      <span style={{ font: f(600, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</span>
                      <span style={{ font: f(500, 10.5), color: C.muted, whiteSpace: 'nowrap' }}>{a.fansly ? 'Fansly' : 'OnlyFans'}</span>
                    </span>
                    <span style={{ display: 'flex', gap: 1, height: 4, borderRadius: 99, background: C.divider, overflow: 'hidden' }}>
                      {barKeys.map((k) => (
                        <span key={k} title={`${t(`sellers.${k}`)} · ${k === 'team' && !hasCrm ? '—' : usd(a.money[k])}`} style={{ height: '100%', width: `${(a.money[k] / maxBar) * 100}%`, background: SELLER_COLOR[k] }} />
                      ))}
                    </span>
                  </span>
                  {cols.map((c, j) => {
                    const v = c.team && !hasCrm ? null : c.value(a);
                    const dim = v === null || v === 0;
                    return <span key={c.id} style={{ textAlign: 'right', font: f(j === 0 ? 600 : 500, j === 0 ? 13 : 12.5), color: dim ? C.faint : j === 0 ? C.text : 'var(--om-body)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{v === null ? '—' : c.show(v)}</span>;
                  })}
                  <Icon name="chevron-right" size={13} color={C.muted} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                </div>
                {isOpen && (
                  <div className="stx-acc-open" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)', gap: 12, padding: '2px 14px 14px 54px', background: bg }}>
                    <div style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '110px repeat(4, minmax(0,1fr))', gap: 10, alignItems: 'center', height: 30, padding: '0 12px', borderBottom: `1px solid ${C.divider}`, font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
                        <span>{t('ledger.metric')}</span>
                        {(['ai', 'team', 'unknown'] as const).map((k) => <span key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: SELLER_COLOR[k] }} />{t(`sellers.${k}`)}</span>)}
                        <span style={{ textAlign: 'right' }}>{t('ledger.total')}</span>
                      </div>
                      {led.map(([label, vals, hi]) => (
                        <div key={label} style={{ display: 'grid', gridTemplateColumns: '110px repeat(4, minmax(0,1fr))', gap: 10, alignItems: 'center', height: 28, padding: '0 12px', background: hi ? C.card : 'transparent' }}>
                          <span style={{ font: f(hi ? 600 : 400, 12), color: hi ? C.text : C.sub, whiteSpace: 'nowrap' }}>{label}</span>
                          {vals.map((v, k) => <span key={k} style={{ textAlign: 'right', font: f(k === 3 || hi ? 600 : 400, 12), color: v === '—' || v === '0' || v === '$0' ? C.faint : k === 3 || hi ? C.text : 'var(--om-body)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{v}</span>)}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 30, padding: '0 12px', borderBottom: `1px solid ${C.divider}` }}>
                        <Icon name="users" size={12} color={C.purplePale} />
                        <span style={{ font: f(600, 12), color: C.text, flex: 1 }}>{t('accounts.teamHere')}</span>
                        {hasCrm && <span style={{ font: f(500, 11.5), color: C.muted }}>{t('accounts.operators', { count: ops.length })}</span>}
                      </div>
                      {hasCrm && ops.map((o) => {
                        const max = Math.max(1, ...ops.map((x) => x.v));
                        return (
                          <div key={o.name} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 56px 72px', gap: 10, alignItems: 'center', height: 28, padding: '0 12px' }}>
                            <span style={{ font: f(500, 12.5), color: 'var(--om-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.name}</span>
                            <span style={{ height: 4, borderRadius: 99, background: C.border, overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: `${(o.v / max) * 100}%`, background: C.purplePale }} /></span>
                            <span style={{ textAlign: 'right', font: f(600, 12.5), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{usd(o.v)}</span>
                          </div>
                        );
                      })}
                      {(!hasCrm || !ops.length) && <div style={{ padding: '10px 12px', font: f(400, 12), color: C.muted }}>{hasCrm ? t('accounts.noOperators') : t('accounts.teamNeedsCrm')}</div>}
                      <span style={{ flex: 1 }} />
                      <button type="button" onClick={() => navigate(`/account/${encodeURIComponent(a.id)}?tab=stats&stab=chat`)} className="cpt-hover" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: 0, borderTop: `1px solid ${C.divider}`, background: 'transparent', font: f(500, 12), color: C.purpleLt, cursor: 'pointer', textAlign: 'left' }}>
                        <Icon name="arrow-up-right" size={12} />{t('accounts.openStats')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!list.length && <div style={{ padding: '18px 14px', borderTop: `1px solid ${C.divider}`, font: f(400, 12.5), color: C.muted }}>{needle ? t('accounts.noMatch') : t('accounts.empty')}</div>}
          {list.length > TOP && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 6, borderTop: `1px solid ${C.divider}` }}>
              <button type="button" onClick={() => setShowAll((v) => !v)} className="cpt-hover" style={{ height: 26, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 5, border: 0, borderRadius: 6, background: 'transparent', color: C.purpleLt, font: f(500, 12), cursor: 'pointer' }}>
                {showAll ? t('accounts.showTop', { count: TOP }) : t('accounts.showAll', { count: list.length })}
                <Icon name="chevron-down" size={12} style={{ transform: showAll ? 'rotate(180deg)' : 'none' }} />
              </button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center', height: 42, padding: '0 14px', borderTop: `1px solid ${C.borderHi}`, background: 'var(--om-card-2)' }}>
            <span />
            <span style={{ font: f(600, 12.5), color: C.text, whiteSpace: 'nowrap' }}>{platform === 'all' && !needle ? t('accounts.allAccounts') : t('accounts.shownAccounts')}</span>
            {cols.map((c) => { const v = total(c); return <span key={c.id} style={{ textAlign: 'right', font: f(600, 12.5), color: v === '—' ? C.faint : C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{v}</span>; })}
            <span />
          </div>
        </div>
      </div>
    </>
  );
}
